import { Injectable } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class SessionStorageService {
    getValue<T>(key: string): T | null {
        return sessionStorage.getItem(key) as unknown as T | null;
    }

    /**
     * Reads a comma separated list of positive integer ids.
     *
     * Replaces a generic `getValues<T>` whose numeric branch was dead: it tested
     * `typeof ({} as T) === 'number'`, and `{} as T` erases to `{}` at runtime, so the check
     * was always `'object'`.  Callers declared `Number[]` but received `string[]`, which made
     * any client-side numeric comparison against these values fail silently (LA-22939).
     *
     * Non-numeric and non-positive entries are dropped rather than surfaced as `NaN` or `0`,
     * so a malformed or half-written key cannot reach the API as a provider id.
     */
    getNumbers(key: string): number[] {
        const value = sessionStorage.getItem(key);
        if (!value) return [];

        return value
            .split(',')
            .map((item) => Number(item.trim()))
            .filter((item) => Number.isInteger(item) && item > 0);
    }

    setValue(key: string, value: string): void {
        sessionStorage.setItem(key, value);
    }

    /**
     * Removes a key outright.  Prefer this over `setValue(key, '')`: an empty string is not
     * absent, and readers that test a key with `!== null` treat it as present (LA-22939).
     */
    removeValue(key: string): void {
        sessionStorage.removeItem(key);
    }
}
